/**
 * LiveKit Token Generator
 *
 * Generates JWT tokens for LiveKit authentication using the jose library.
 *
 * ⚠️ SECURITY WARNING:
 * This implementation generates tokens on the client-side for development only.
 * In production, token generation MUST be moved to your backend server.
 *
 * The token contains permissions (canPublish, canSubscribe) and participant identity.
 * Each participant must have a unique identity to avoid conflicts.
 */
import { jwtVerify, SignJWT } from 'jose';

/**
 * Generate a LiveKit access token
 *
 * WARNING: This is for development/testing only.
 * In production, tokens should be generated on your backend server.
 */
export const generateLiveKitToken = async (
  apiKey: string,
  apiSecret: string,
  roomName: string,
  participantName: string,
  options: {
    canPublish?: boolean;
    canSubscribe?: boolean;
    canPublishData?: boolean;
    exp?: number;
  } = {}
): Promise<string> => {
  const {
    canPublish = true,
    canSubscribe = true,
    canPublishData = true,
    exp = Math.floor(Date.now() / 1000) + 3600, // 1 hour
  } = options;

  // Convert API secret to Uint8Array for jose
  const secretKey = new TextEncoder().encode(apiSecret);

  // Create JWT payload according to LiveKit token format
  const payload = {
    // Standard JWT claims (RFC 7519)
    iss: apiKey, // Issuer: Your LiveKit API key
    sub: participantName, // Subject: Participant identity (MUST be unique!)
    jti: `${participantName}-${Date.now()}`, // JWT ID: Unique token identifier
    exp, // Expiration: When token expires
    nbf: Math.floor(Date.now() / 1000), // Not Before: Token valid from now
    iat: Math.floor(Date.now() / 1000), // Issued At: When token was created

    // LiveKit-specific video permissions
    video: {
      room: roomName, // Room name to join
      roomJoin: true, // Allow joining the room
      canPublish, // Can publish audio/video streams
      canSubscribe, // Can receive audio/video streams
      canPublishData, // Can send data messages
    },

    // Participant metadata
    name: participantName, // Display name
    metadata: '', // Custom metadata (JSON string)
  };

  try {
    // Sign the JWT
    const jwt = await new SignJWT(payload)
      .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
      .setIssuedAt()
      .setExpirationTime(exp)
      .sign(secretKey);

    return jwt;
  } catch (error) {
    console.error('Error generating LiveKit token:', error);
    throw new Error('Failed to generate LiveKit token');
  }
};

/**
 * Verify a LiveKit token (for testing)
 */
export const verifyLiveKitToken = async (token: string, apiSecret: string): Promise<any> => {
  try {
    const secretKey = new TextEncoder().encode(apiSecret);
    const { payload } = await jwtVerify(token, secretKey);
    return payload;
  } catch (error) {
    console.error('Error verifying LiveKit token:', error);
    throw new Error('Invalid LiveKit token');
  }
};
